import { supabase } from '@/integrations/supabase/client';

export type GammaFormat = 'presentation' | 'resumen' | 'infografia';

interface GenerateGammaParams {
  content?: string;
  topicId?: string;
  topicQuery?: string;
  userQuestion?: string;
  format: GammaFormat;
  autonomousCommunity?: string;
  examType?: string;
}

interface GammaResult {
  exportUrl?: string;
  gammaUrl?: string; // Ahora es opcional si es local
  markdown?: string; // Nuevo
  isLocal?: boolean; // Nuevo
  deck?: any; // Objeto estructurado MunilexDeck
  isMunilexDeck?: boolean; // Indicador de motor nativo
  message?: string; // Nuevo
  credits?: { deducted: number; remaining: number };
  format: GammaFormat | 'munilex_deck';
}

export async function generateGammaDocument(params: GenerateGammaParams): Promise<GammaResult> {
  if (params.format === 'resumen') {
    throw new Error('Los resúmenes no se generan en Gamma. Usa el flujo de resumen del VPS.');
  }

  // Use getUser() to ensure a fresh session and trigger auto-refresh if needed
  const { data: { user }, error: authError } = await supabase.auth.getUser();
  const { data: { session } } = await supabase.auth.getSession();
  const accessToken = session?.access_token;

  if (authError || !accessToken) {
    console.error('Auth error in generateGamma:', authError);
    throw new Error('No autorizado o sesión expirada. Inicia sesión para continuar.');
  }

  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), 300000); // 5 minutes timeout

  try {
    const response = await fetch(
      `${import.meta.env.VITE_API_URL}/generate-gamma`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${accessToken}`,
        },
        body: JSON.stringify(params),
        signal: controller.signal,
      }
    );

    clearTimeout(timeoutId);

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ error: 'Error desconocido' }));
      throw new Error(errorData.error || `Error ${response.status}`);
    }

    return response.json();
  } catch (error: any) {
    clearTimeout(timeoutId);
    if (error.name === 'AbortError') {
      throw new Error('La generación está tardando más de lo esperado debido a la alta demanda. Por favor, inténtalo de nuevo en unos momentos.');
    }
    throw error;
  }
}

export function getFormatLabel(format: GammaFormat): string {
  const labels: Record<GammaFormat, string> = {
    presentation: 'Presentación',
    resumen: 'Resumen',
    infografia: 'Infografía',
  };
  return labels[format];
}

export function downloadFromUrl(url: string, filename: string) {
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  link.target = '_blank';
  link.rel = 'noopener noreferrer';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
}
